import { Controller } from "@hotwired/stimulus"
import { patchJson, notifySaveFailure } from "../lib/routing_patch"

// Per alert-card controller. The cascade is display-only — it flips .alert-off /
// .section-off / .source-off classes that CSS acts on — while each control
// independently PATCHes its row. Optimistic: the DOM flips immediately, the
// request follows, and a failure reverts the control and toasts. All routing
// *correctness* lives server-side in Notifications::Resolution; this only mirrors it.
export default class extends Controller {
  static targets = [ "alertBlock" ]
  static values = { routesUrl: String, alertUrl: String, alertId: String }

  stop(event) {
    event.stopPropagation()
  }

  async toggleAlert(event) {
    const input = event.target
    const checked = input.checked
    this.alertBlockTarget.classList.toggle("alert-off", !checked)

    try {
      await patchJson(this.alertUrlValue, { is_notifications_enabled: checked })
    } catch {
      input.checked = !checked
      this.alertBlockTarget.classList.toggle("alert-off", checked)
      notifySaveFailure()
    }
  }

  async toggleSection(event) {
    const input = event.target
    const checked = input.checked
    // The section toggle sits in the card's <summary>, a sibling of the block, so
    // reach the block through the controller element rather than closest().
    const block = this.element.querySelector(".routing-dest-block")
    block.classList.toggle("section-off", !checked)

    try {
      await patchJson(this.routesUrlValue, {
        alert_id: this.alertIdValue,
        notification_channel_id: input.dataset.channelId,
        is_enabled: checked
      })
    } catch {
      input.checked = !checked
      block.classList.toggle("section-off", checked)
      notifySaveFailure()
    }
  }

  async toggleSource(event) {
    const input = event.target
    const checked = input.checked
    const row = input.closest(".routing-srow")
    const select = row.querySelector("select")
    row.classList.toggle("source-off", !checked)
    if (select) select.disabled = !checked

    try {
      await patchJson(this.routesUrlValue, {
        alert_id: this.alertIdValue,
        notification_channel_id: input.dataset.channelId,
        platform_id: input.dataset.platformId,
        is_enabled: checked
      })
    } catch {
      input.checked = !checked
      row.classList.toggle("source-off", checked)
      if (select) select.disabled = checked
      notifySaveFailure()
    }
  }

  // "Apply to all platforms" — a deliberate action button, not a select-change
  // handler, so picking a channel doesn't silently overwrite every platform row.
  async applySectionChannel(event) {
    const button = event.currentTarget
    const select = button.closest(".routing-dest-head").querySelector("select")
    const label = this.#label(select)
    button.disabled = true

    try {
      await patchJson(this.routesUrlValue, {
        alert_id: this.alertIdValue,
        notification_channel_id: select.dataset.channelId,
        destination: select.value,
        destination_label: label
      })
      select.classList.remove("routing-inherited")
      select.classList.add("routing-override")
      this.#applyToInheritedSources(select.value, label)
      window.toast(`Applied ${label} to all platforms`, { kind: "success" })
    } catch {
      notifySaveFailure()
    } finally {
      button.disabled = false
    }
  }

  async changeSourceChannel(event) {
    const select = event.target

    try {
      const response = await patchJson(this.routesUrlValue, {
        alert_id: this.alertIdValue,
        notification_channel_id: select.dataset.channelId,
        platform_id: select.dataset.platformId,
        destination: select.value,
        destination_label: this.#label(select)
      })
      const { style } = await response.json()
      select.classList.toggle("routing-override", style === "override")
      select.classList.toggle("routing-inherited", style !== "override")
    } catch {
      notifySaveFailure()
    }
  }

  #label(select) {
    return select.selectedOptions[0]?.text || ""
  }

  // Platform rows still following the default (no override of their own) mirror
  // the new "apply to all" channel immediately; overridden rows are untouched.
  #applyToInheritedSources(value, label) {
    this.element.querySelectorAll(".routing-srow select.routing-inherited").forEach((select) => {
      let option = Array.from(select.options).find((opt) => opt.value === value)
      if (!option) {
        option = document.createElement("option")
        option.value = value
        select.appendChild(option)
      }
      option.textContent = label
      select.value = value
    })
  }
}
